import { apiClient } from './api';
import { UrlBuilder } from './urls';

/**
 * Flutterwave Payment Integration
 * Handles generating payment links for credit points and subscriptions
 */

export interface FlutterwaveGenerateLinkParams {
  user_id: number;
  amount: number; // Amount (unit depends on currency)
  credit_points: number;
  redirect_url: string;
}

export interface FlutterwaveSubscriptionGenerateLinkParams {
  user_id: number;
  subscription_id: number;
  redirect_url: string;
}

export interface FlutterwavePaymentLinkResponse {
  payment_link: string;
  tx_ref: string;
  status: string;
  message: string;
  subscription?: {
    id: number;
    subscription_name: string;
    subscription_type: string;
    subscription_duration: number;
    subscription_price: number;
  };
}

/**
 * Generate Flutterwave payment link for credit points purchase
 */
export async function generateFlutterwavePaymentLink(
  token: string,
  params: FlutterwaveGenerateLinkParams
): Promise<FlutterwavePaymentLinkResponse> {
  const response = await apiClient.postWithToken<FlutterwavePaymentLinkResponse>(
    UrlBuilder.credits.flutterwaveGenerateLink(),
    token,
    params
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to generate Flutterwave payment link');
  }

  return response.data as FlutterwavePaymentLinkResponse;
}

/**
 * Generate Flutterwave payment link for subscription purchase
 */
export async function generateFlutterwaveSubscriptionLink(
  token: string,
  params: FlutterwaveSubscriptionGenerateLinkParams
): Promise<FlutterwavePaymentLinkResponse> {
  const response = await apiClient.postWithToken<FlutterwavePaymentLinkResponse>(
    UrlBuilder.credits.flutterwaveSubscriptionGenerateLink(),
    token,
    params
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to generate Flutterwave subscription payment link');
  }

  return response.data as FlutterwavePaymentLinkResponse;
}

/**
 * Redirect to payment link in same tab
 */
export function redirectToPaymentLink(paymentLink: string): void {
  window.location.href = paymentLink;
}

/**
 * Try to close the payment window if it's still open
 */
export function closePaymentWindow(paymentWindow: Window | null): void {
  if (paymentWindow && !paymentWindow.closed) {
    try {
      paymentWindow.close();
    } catch (error) {
      // Some browsers may block closing windows not opened by script
      console.log('Could not close payment window:', error);
    }
  }
}